import { CardMedia, Divider, IconButton, makeStyles, Typography } from "@material-ui/core";
import { MdArrowBack } from "react-icons/md";
import { AiOutlineInfoCircle } from "react-icons/ai";
import CustomCard from "../CustomCard";
import QRCodeButton from "../QRCodeButton";
import { useEffect, useState } from "react";
import ErrorPage from "../ErrorPage/ErrorPage";
import { useVerified } from "../../hooks/useVerified";
import { useStationData } from "../../hooks/useStationData";
import verifiedSVG from "../../images/verifiedSVG.svg";
import { GET_EXERCISES } from "../../GraphQL/Queries";
import { useQuery } from "@apollo/client";
import BadgePopup from "../Badges/BadgePopup";
import { getBadgeDetails } from "../Badges/BadgeData";
import { zeroPad } from "../../lib/utilities";
import { getGermanDate } from "../../lib/utilities";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: 70,
  },
  header: {
    padding: theme.spacing(0, 2),
  },
  cover: {
    position: "fixed",
    zIndex: 1,
    borderRadius: 0,
    filter: "brightness(50%)",
  },
  arrowBack: {
    position: "fixed",
    zIndex: 3,
  },
  title: {
    display: "flex",
    alignItems: "center",
    position: "fixed",
    zIndex: 2,
    paddingTop: 130,
    fontWeight: 600,
    color: "#fff",
    letterSpacing: "0.6px",
  },
  container: {
    margin: theme.spacing(0, 2),
    paddingTop: 200,
  },
  exercise: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    backgroundColor: theme.palette.primary.main,
    width: "2px",
  },
  duration: {
    color: theme.palette.grey[600],
  },
  activityDetails: {
    display: "flex",
  },
  activityExercises: {
    display: "flex",
    flexDirection: "column",
    marginLeft: theme.spacing(1),
  },
  info: {
    display: "flex",
    marginTop: theme.spacing(2),
    justifyContent: "center",
    alignItems: "center",
  },
  infoIcon: {
    fontSize: "1.5rem",
    marginRight: theme.spacing(1),
  },
  verifiedSVG: {
    marginLeft: 5,
  },
  mono: {
    fontFamily: "'Roboto Mono', Inter, monospace",
    display: "inline-block",
    minWidth: 15,
  },
}));

function Station({ stNr, navigate, location }) {
  const classes = useStyles();

  const [userActivities] = useStationData(stNr, location?.state?.update);
  const [station, setStation] = useState();
  const [badgeContent, setBadgeContent] = useState({
    open: false,
    title: "",
    text: "",
    id: 1,
    amount: "",
    unit: "",
  });

  const { verifiedString, isVerified } = useVerified();

  const { data, isLoading, error } = useQuery(GET_EXERCISES, {
    variables: { stNr: parseInt(stNr) },
  });

  useEffect(() => {
    if (!isLoading && data) {
      setStation(data.stationCollection.items[0]);
    }
  }, [isLoading, data]);

  useEffect(() => {
    // Ausbaustufe: Eine Zusammenfassung aller ereichten Badges im Popup
    if (location?.state?.badgeData) {
      const { badgeData } = location.state;
      badgeData.slice(-1)[0] && setBadgeContent(badgeData.slice(-1)[0]);
    }
  }, [location?.state?.badgeData, location?.state]);

  if (error || data?.stationCollection.items.length === 0) return <ErrorPage />;

  if (isLoading || !data || !station) return null;

  const { uebungenCollection: { items: exercises } = [] } = station;

  const renderActivities = () => {
    if (userActivities) {
      const activities = userActivities.map((workoutDay) => {
        const { date, exercises } = workoutDay;
        const dateString = getGermanDate(date, true);
        return (
          <div key={dateString}>
            <Typography variant="caption" color="primary" style={{ textTransform: "uppercase" }}>
              {dateString}
            </Typography>

            <div className={classes.activityDetails}>
              <Divider orientation="vertical" flexItem className={classes.divider} />
              <span className={classes.activityExercises}>
                {exercises &&
                  exercises.map((exercise, idx) => {
                    const duration = new Date(exercise.duration * 1000).toISOString().substr(14, 5);
                    return (
                      <Typography variant="caption" key={idx}>
                        <span className={`${classes.repetition} ${classes.mono}`}>
                          {zeroPad(exercise.repetition, 2)}
                        </span>{" "}
                        {`${exercise.name} `}
                        <span className={classes.duration}>({duration})</span>
                      </Typography>
                    );
                  })}
              </span>
            </div>
          </div>
        );
      });
      return activities;
    }
  };

  return (
    <div className={classes.root}>
      {badgeContent.open && (
        <BadgePopup
          open={badgeContent.open}
          badgeDetails={{
            color: getBadgeDetails(badgeContent.id).color,
            title: badgeContent.title,
            longDescription: badgeContent.text,
            icon: getBadgeDetails(badgeContent.id).icon,
          }}
          amount={badgeContent.amount} //aus badgeLogic
          unit={badgeContent.unit}
          achieved={getGermanDate()}
          close={() =>
            setBadgeContent({ open: false, text: "", title: "", amount: "", unit: "", id: "" })
          }
        />
      )}
      <QRCodeButton showText />
      <CardMedia
        component="img"
        className={classes.cover}
        alt={`Station ${stNr}`}
        height="170"
        image={station.stationsbild.url}
        title={`Station ${stNr}`}
      />
      <div className={classes.header}>
        <IconButton
          edge="start"
          color="inherit"
          aria-label="zurück"
          className={classes.arrowBack}
          onClick={async function () {
            // navigate(-1) seems to be bugged in this case, see:
            // https://github.com/reach/router/issues/85
            await navigate("/stationen");
          }}
        >
          <MdArrowBack style={{ color: "#fff" }} />
        </IconButton>
        <Typography variant="h5" className={classes.title}>
          {`${stNr}. ${station.stationsname} `}
          {isVerified && (
            <img src={verifiedSVG} alt="verified svg" height="20" className={classes.verifiedSVG} />
          )}
        </Typography>
      </div>
      <div className={classes.container}>
        <div className={classes.exercise}>
          <Typography variant="h6">Übungen</Typography>
          {exercises.map((ex, idx) => (
            <CustomCard
              key={idx}
              description={`Klicke hier um mit ${ex.titel} zu starten`}
              name={ex.titel}
              img={ex?.thumbnail?.url}
              navigate={navigate}
              verifiedString={verifiedString}
            />
          ))}
        </div>
        <div>
          <Typography variant="h6">Letzte Aktivitäten</Typography>
          <div className={classes.activity}>
            {userActivities?.length > 0 ? (
              renderActivities()
            ) : (
              <Typography variant="subtitle1" color="textSecondary" className={classes.info}>
                <AiOutlineInfoCircle className={classes.infoIcon} /> Noch keine Aktivitäten
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Station;
